import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap, catchError, throwError, map, shareReplay, BehaviorSubject } from 'rxjs';
import { Firestore, doc, onSnapshot, getDoc } from '@angular/fire/firestore';
import { environment } from 'src/environments/environment';
import { PhotoService } from './photo.service';
import { WebStorage } from '../components/auth/storage/web.storage';
import { EntityRelation } from '../model/entity-relation.model';


@Injectable({
  providedIn: 'root'
})
export class EntityApiService {
  private firestore = inject(Firestore);
  private cache = new Map<string, EntityRelation>();
  private subjects = new Map<string, BehaviorSubject<EntityRelation>>();
  private ongoingRequests = new Map<string, BehaviorSubject<EntityRelation>>();
  private cachedVersion = new Map<string, number>();
  private versionListeners = new Map<string, () => void>();

  constructor(
    private http: HttpClient,
    private photoService: PhotoService,
    private webStorage: WebStorage // Inject WebStorageService or appropriate service
  ) {}

  load(schoolId: string | number): BehaviorSubject<EntityRelation> {
    const key = String(schoolId);

    if (!this.subjects.has(key)) {
      this.subjects.set(key, new BehaviorSubject<EntityRelation>({} as EntityRelation));
    }
    const subject = this.subjects.get(key)!;

    if (this.ongoingRequests.has(key)) {
      return subject;
    }

    this._ensureVersionListener(key);

    if (this.cache.has(key)) {
      const cached = this.cache.get(key)!;
      if (subject.value !== cached) {
        subject.next(cached);
      }
      this._checkVersionAndRefreshIfNeeded(key, subject);
      return subject;
    }

    this._checkVersionAndRefreshIfNeeded(key, subject);
    return subject;
  }

  forceRefresh(schoolId: string | number): void {
    const key = String(schoolId);
    if (this.ongoingRequests.has(key)) return;
    this.cache.delete(key);
    this.cachedVersion.delete(key);
    this.load(key);
  }

  cleanupVersionListeners(schoolId?: string | number): void {
    if (schoolId !== undefined) {
      const key = String(schoolId);
      this.versionListeners.get(key)?.();
      this.versionListeners.delete(key);
    } else {
      this.versionListeners.forEach(unsub => unsub());
      this.versionListeners.clear();
    }
  }

  private async _checkVersionAndRefreshIfNeeded(
    key: string,
    subject: BehaviorSubject<EntityRelation>
  ): Promise<void> {
    if (this.ongoingRequests.has(key)) return;
    try {
      const snap = await getDoc(doc(this.firestore, 'entityRelationVersion', key));
      const remoteVersion = snap.exists() ? (snap.data()['version'] as number) : null;
      const localVersion = this.cachedVersion.get(key);

      const isFresh =
        this.cache.has(key) &&
        localVersion !== undefined &&
        remoteVersion !== null &&
        localVersion === remoteVersion;

      if (isFresh) {
        const cached = this.cache.get(key)!;
        if (subject.value !== cached) subject.next(cached);
        return;
      }

      this._fetchFromApi(key, subject, remoteVersion ?? undefined);
    } catch {
      if (!this.cache.has(key)) {
        this._fetchFromApi(key, subject, undefined);
      }
    }
  }

  private _fetchFromApi(
    key: string,
    subject: BehaviorSubject<EntityRelation>,
    version: number | undefined
  ): void {
    if (this.ongoingRequests.has(key)) return;
    this.ongoingRequests.set(key, subject);

    this.http.get<EntityRelation>(
      `${environment.serviceUrl}/schools/${key}/entity-relation`
    ).pipe(
      tap(data => {
        subject.next(data);
        this.cache.set(key, data);
        if (version !== undefined) this.cachedVersion.set(key, version);
        this.ongoingRequests.delete(key);
      }),
      catchError(err => {
        this.ongoingRequests.delete(key);
        if (!this.cache.has(key)) subject.error(err);
        return throwError(() => err);
      })
    ).subscribe();
  }

  private _ensureVersionListener(key: string): void {
    if (this.versionListeners.has(key)) return;

    const unsub = onSnapshot(
      doc(this.firestore, 'entityRelationVersion', key),
      snap => {
        if (!snap.exists()) return;
        const remoteVersion = snap.data()['version'] as number;
        const localVersion = this.cachedVersion.get(key);

        if (localVersion === undefined || remoteVersion === localVersion) return;

        const subject = this.subjects.get(key);
        if (!subject || this.ongoingRequests.has(key)) return;

        this.cache.delete(key);
        this.cachedVersion.delete(key);
        this._fetchFromApi(key, subject, remoteVersion);
      },
      err => console.error('[EntityApiService] Version listener error', err)
    );

    this.versionListeners.set(key, unsub);
  }

  resolveParticipant(participantKey: string, schoolId: string): { name: string; photoUrl: string | null; role: string; designation?: string; email?: string; } {
    const [role, id] = participantKey.split('_');
    const data = this.cache.get(schoolId);
    if (!data) {
      throw new Error(`School data not loaded for ${schoolId}`);
    }

    if (role === 'staff') {
      const staff = data.staff.find(s => s.id === Number(id));
      if (staff) {
        return {
          name: staff.name,
          photoUrl: staff.photoUuid ? `${environment.serviceUrl}/documents-download/${staff.photoUuid}` : null,
          role: 'staff',
          designation: staff.designation,
          email: staff.email
        };
      }
    } else if (role === 'guardian') {
      const guardian = data.guardians.find(g => g.id === Number(id));
      if (guardian) {
        return {
          name: guardian.name,
          photoUrl: guardian.photoUuid ? `${environment.serviceUrl}/documents-download/${guardian.photoUuid}` : null,
          role: 'guardian',
          email: guardian.email
        };
      }
    }

    throw new Error(`Participant ${participantKey} not found`);
  }

  getStudentName(studentId: string, schoolId: string): string {
    const data = this.cache.get(schoolId);
    const student = data?.students.find(s => s.id.toString() === studentId);
    return student?.name || '';
  }

  getStudentRoom(studentId: string, schoolId: string): string {
    const data = this.cache.get(schoolId);
    const student = data?.students.find(s => s.id.toString() === studentId);
    const room = data?.rooms.find(r => r.id === student?.roomId);
    return room?.name || '';
  }
  
  getParticipantKeysForType(
    type: string,
    studentId: string,
    schoolId: string,
    guardianId?: string
  ): string[] {
    const data = this.cache.get(schoolId);
    if (!data) return [];
  
    const student = data.students.find(s => s.id.toString() === studentId);
    if (!student) return [];
  
    const ADMIN_DESIGNATIONS = new Set(['admin', 'director', 'owner']);
  
    // Helper functions — lazy, only called when needed
    const getAllGuardianKeys = () =>
      student.guardianIds.map(id => `guardian_${id}`);
  
    const getSingleGuardianKey = () =>
      guardianId ? [`guardian_${guardianId}`] : [];
  
    const getRoomStaffKeys = () =>
      data.staff
        .filter(staff => staff.roomIds.includes(student.roomId) && staff.designation.toLowerCase() !== 'kiosk')
        .map(staff => `staff_${staff.id}`);
  
    const getAdminStaffKeys = () =>
      data.staff
        .filter(staff => ADMIN_DESIGNATIONS.has(staff.designation.toLowerCase()))
        .map(staff => `staff_${staff.id}`);
  
    switch (type) {
      case 'studentGroup':
        return [...getAllGuardianKeys(), ...getRoomStaffKeys()];
  
      case 'studentPrivate':
        return [...getSingleGuardianKey(), ...getRoomStaffKeys()];
  
      case 'adminGroup':
        return [...getAllGuardianKeys(), ...getAdminStaffKeys()];
  
      case 'adminPrivate':
        return [...getSingleGuardianKey(), ...getAdminStaffKeys()];
  
      case 'directMessage':
        return [];
  
      default:
        return [];
    }
  }

  getParticipantKeysForRoom(roomId: number, schoolId: string): string[] {
    const data = this.cache.get(schoolId);
    if (!data) return [];
    return data.staff
      .filter(s =>
        s.designation?.toLowerCase() !== 'kiosk' &&
        s.profileStatus !== 'I' &&
        (s.roomIds?.includes(roomId) || s.primaryRoomId === roomId)
      )
      .map(s => `staff_${s.id}`);
  }

  resolveInactiveStaffById(
    staffId: number,
    schoolId: string
  ): { name: string; photoUrl: string | null; designation?: string } | null {
    const data = this.cache.get(schoolId);
    if (!data) return null;
    const member = data.staff.find(s => s.id === staffId && s.profileStatus === 'I');
    if (!member) return null;
    return {
      name: member.name,
      photoUrl: member.photoUuid
        ? `${environment.serviceUrl}/documents-download/${member.photoUuid}`
        : null,
      designation: member.designation
    };
  }

  // getParticipantKeysForType(
  //   type: string,
  //   studentId: string,
  //   schoolId: string,
  //   guardianId?: string
  // ): string[] {
  //   const data = this.cache.get(schoolId);
  //   if (!data) return [];

  //   const student = data.students.find(s => s.id.toString() === studentId);
  //   if (!student) return [];

  //   switch (type) {
  //     case 'studentGroup':
  //       const guardianKeys = student.guardianIds.map(id => `guardian_${id}`);
  //       const staffKeys = data.staff
  //         .filter(staff => staff.roomIds.includes(student.roomId))
  //         .map(staff => `staff_${staff.id}`);
  //       return [...guardianKeys, ...staffKeys];

  //     case 'studentPrivate':
  //       const guardianKey = guardianId ? [`guardian_${guardianId}`] : [];
  //       const staffKeysPrivate = data.staff
  //         .filter(staff => staff.roomIds.includes(student.roomId))
  //         .map(staff => `staff_${staff.id}`);
  //       return [...guardianKey, ...staffKeysPrivate];

  //     case 'adminGroup':
  //       const adminGuardianKeys = student.guardianIds.map(id => `guardian_${id}`);
  //       const adminKeys = data.staff
  //       .filter(staff => staff.designation.toLowerCase() == 'admin' || staff.designation.toLowerCase() == 'director' || staff.designation.toLowerCase() == 'owner')
  //       .map(staff => `staff_${staff.id}`);
  //       return [...adminGuardianKeys, ...adminKeys];

  //     case 'adminPrivate':
  //       const adminGuardianKey = guardianId ? [`guardian_${guardianId}`] : [];
  //       const adminKeysPrivate = data.staff
  //         .filter(staff => staff.designation.toLowerCase() == 'admin' || staff.designation.toLowerCase() == 'director' || staff.designation.toLowerCase() == 'owner')
  //         .map(staff => `staff_${staff.id}`);
  //       return [...adminGuardianKey, ...adminKeysPrivate];

  //     case 'directMessage':
  //       // This should be handled by FirebaseBatchService.buildParticipantKeys with staffKeyA and staffKeyB
  //       return [];

  //     default:
  //       return [];
  //   }
  // }

  // Legacy methods for backward compatibility
  getRooms(): Observable<EntityRelation['rooms']> {
    const user = this.webStorage.getUserData();
    return this.load(user.schoolId).pipe(
      map(data => data.rooms)
    );
  }

  getStudents(): Observable<EntityRelation['students']> {
    const user = this.webStorage.getUserData();
    return this.load(user.schoolId).pipe(
      map(data => data.students)
    );
  }

  getGuardians(): Observable<EntityRelation['guardians']> {
    const user = this.webStorage.getUserData();
    return this.load(user.schoolId).pipe(
      map(data => data.guardians)
    );
  }

  getStaff(): Observable<EntityRelation['staff']> {
    const user = this.webStorage.getUserData();
    return this.load(user.schoolId).pipe(
      map(data => data.staff)
    );
  }

  // Method used by sidebar facade
  getEntityRelation(): BehaviorSubject<EntityRelation> {
    const user = this.webStorage.getUserData();
    return this.load(user.schoolId);
  }
}